import { getReturnMediaFileValidationError } from "./returnMedia";

export const RETURN_IMAGE_QC_UPLOAD_ACTION = "submit_return_images";
export const RETURN_IMAGE_QC_CAPTURE_CODE = "other";
export const RETURN_IMAGE_QC_MAX_IMAGES = 20;

const normalizeId = (value) => String(value ?? "").trim();

const getReturnImageQCAlertIds = (alert = {}) =>
  [alert.context?.return_item_id, alert.cta?.resource_id, alert.source_id]
    .map(normalizeId)
    .filter(Boolean);

const getReturnImageQCLineItemIds = (lineItem = {}) =>
  [
    lineItem.line_item_id,
    lineItem.id,
    lineItem.return_request_line_item_id,
  ]
    .map(normalizeId)
    .filter(Boolean);

export function clearReturnImageQCUploadedMediaIds(
  uploadedMediaIdsByItem = {},
  itemKey,
) {
  return { ...uploadedMediaIdsByItem, [itemKey]: [] };
}

export function validateReturnImageQCFile(file = {}) {
  return getReturnMediaFileValidationError(file, "image");
}

export function isReturnImageQCUploadAlert(alert = {}) {
  const action = String(alert.action || alert.cta?.action || "")
    .trim()
    .toLowerCase();
  const target = String(alert.cta?.target || "").trim().toLowerCase();

  return (
    action === RETURN_IMAGE_QC_UPLOAD_ACTION ||
    target === "return_image_qc_upload"
  );
}

export function isReturnImageQCAlertForLineItem(alert = {}, lineItem = {}) {
  if (!isReturnImageQCUploadAlert(alert)) {
    return false;
  }

  const alertIds = getReturnImageQCAlertIds(alert);
  const lineItemIds = getReturnImageQCLineItemIds(lineItem);

  return alertIds.some((alertId) => lineItemIds.includes(alertId));
}

export function getReturnImageQCLineItemId(alert = {}, lineItem = {}) {
  const canonicalLineItemId = normalizeId(lineItem.line_item_id);
  if (canonicalLineItemId) {
    return canonicalLineItemId;
  }

  const alertIds = getReturnImageQCAlertIds(alert);
  const lineItemIds = getReturnImageQCLineItemIds(lineItem);
  return (
    lineItemIds.find((lineItemId) => alertIds.includes(lineItemId)) ||
    lineItemIds[0] ||
    alertIds[0] ||
    ""
  );
}

export function findReturnImageQCUploadAlert(alerts = [], lineItem = {}) {
  return alerts.find((alert) =>
    isReturnImageQCAlertForLineItem(alert, lineItem),
  );
}

export function markReturnImageQCLineItemPending(
  lineItems = [],
  alert = {},
  attempt,
) {
  return lineItems.map((lineItem) =>
    isReturnImageQCAlertForLineItem(alert, lineItem)
      ? {
          ...lineItem,
          image_qc_status: "qc_pending",
          image_qc_attempt: attempt,
          image_qc_decision: null,
          image_qc_summary: null,
          image_qc_failure_reasons: null,
          image_qc_coverage_summary: null,
        }
      : lineItem,
  );
}

export function getReturnImageQCMissingChecks(alert = {}) {
  return Array.isArray(alert.context?.missing_checks)
    ? alert.context.missing_checks
        .map((check) => String(check || "").trim())
        .filter(Boolean)
    : [];
}

export function getReturnImageQCFailureMessages(alert = {}) {
  return Array.isArray(alert.context?.failure_reasons)
    ? alert.context.failure_reasons
        .map((reason) => String(reason?.message || "").trim())
        .filter(Boolean)
    : [];
}

export function buildReturnImageQCResubmissionPayload({
  returnRequestId,
  lineItemId,
  uploadedMediaIds,
}) {
  const requestId = Number(returnRequestId);
  const sourceLineItemId = normalizeId(lineItemId);
  const mediaIds = Array.from(
    new Set((uploadedMediaIds || []).map(Number).filter((id) => id > 0)),
  );

  if (!Number.isInteger(requestId) || requestId <= 0) {
    throw new Error("A valid return request is required to submit images.");
  }
  if (!sourceLineItemId) {
    throw new Error("A Shopify line item ID is required to submit images.");
  }
  if (mediaIds.length === 0) {
    throw new Error("At least one uploaded image is required.");
  }

  return {
    return_request_id: requestId,
    source_line_item_id: sourceLineItemId,
    uploaded_media_ids: mediaIds,
    capture_code: RETURN_IMAGE_QC_CAPTURE_CODE,
  };
}
